import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import DoubtSolver from "@/components/DoubtSolver";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Lightbulb,
  Target,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Mic,
} from "lucide-react";

const SECTION_HINTS = {
  "Clinical Scenario": "What the patient looks like in front of you.",
  "History Taking (HOPC)": "Questions you must ask - and what the answers tell you.",
  "Investigations": "Tests and X-rays you need, and what the findings mean.",
  "Differential Diagnosis": "What else could it be - and how to rule each one out.",
  "The Diagnosis Explained": "The diagnosis, named - and the pathophysiology behind it.",
  "Management": "How to treat the patient - step by step, with the why.",
};

const ScenarioDetailPage = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();
  const [scenario, setScenario] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizIdx, setQuizIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/scenarios/${scenarioId}`);
        setScenario(res.scenario);
        setQuestions(res.questions || []);
      } catch (e) {
        console.error(e);
        toast.error("Could not load this case.");
        navigate("/cases");
      } finally {
        setLoading(false);
      }
    })();
  }, [scenarioId, navigate]);

  const handleComplete = async () => {
    try {
      await api.post(`/scenarios/${scenarioId}/complete`, {});
      setScenario((s) => ({ ...s, completed: true }));
      toast.success("Case completed! +30 points");
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading case...</p>
        </div>
      </div>
    );
  }

  if (!scenario) return null;

  const sections = scenario.sections || [];
  const totalSections = sections.length;
  const isLastSection = currentSection === totalSections - 1;
  const sectionData = sections[currentSection];
  const progress = ((currentSection + 1) / Math.max(totalSections, 1)) * 100;

  // Quiz UI
  const currentQ = questions[quizIdx];
  const handleQuizSubmit = () => {
    if (!selected || !currentQ) return;
    setAnswered(true);
    if (selected === currentQ.correct_option_id) {
      setScore((s) => s + 1);
    }
  };
  const handleQuizNext = () => {
    if (quizIdx < questions.length - 1) {
      setQuizIdx(quizIdx + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      toast.success(`Case quiz done! Score: ${score + (currentQ && selected === currentQ.correct_option_id ? 1 : 0)}/${questions.length}`);
      navigate("/cases");
    }
  };

  // ===== QUIZ MODE =====
  if (showQuiz && questions.length > 0) {
    return (
      <div data-testid="scenario-quiz" className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowQuiz(false)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to case notes
        </Button>

        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Case Quiz: {scenario.title}</h2>
          <span className="text-sm text-muted-foreground">
            Question {quizIdx + 1} of {questions.length}
          </span>
        </div>
        <div className="w-full h-2 bg-secondary rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((quizIdx + 1) / questions.length) * 100}%` }}
          />
        </div>

        {currentQ && (
          <div className="glass rounded-2xl p-6 border border-border">
            <h3 className="text-lg font-medium mb-5">{currentQ.question_text}</h3>
            <div className="space-y-2.5">
              {currentQ.options.map((opt) => {
                const isSelected = selected === opt.id;
                const isCorrect =
                  answered && opt.id === currentQ.correct_option_id;
                const isWrong =
                  answered && isSelected && opt.id !== currentQ.correct_option_id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`scenario-option-${opt.id}`}
                    onClick={() => !answered && setSelected(opt.id)}
                    disabled={answered}
                    className={`w-full text-left p-4 rounded-xl border transition-all
                      ${isSelected && !answered ? "border-primary bg-primary/10" : "border-border"}
                      ${isCorrect ? "border-success bg-success/10" : ""}
                      ${isWrong ? "border-destructive bg-destructive/10" : ""}
                      ${!answered ? "hover:border-primary/50" : ""}
                    `}
                  >
                    <span className="font-medium mr-2">{opt.id.toUpperCase()})</span>
                    {opt.text}
                  </button>
                );
              })}
            </div>

            {answered && (
              <div className="mt-5 p-4 rounded-xl bg-secondary border border-border">
                <p className="text-sm font-medium mb-1 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-warning" />
                  {selected === currentQ.correct_option_id ? "Correct!" : "Not quite."}
                </p>
                <p className="text-sm text-muted-foreground">{currentQ.explanation}</p>

                {/* AI Doubt Solver */}
                <DoubtSolver
                  questionId={currentQ.id}
                  selectedOptionId={selected}
                  testIdPrefix="scenario-doubt"
                />
              </div>
            )}

            <div className="flex justify-end mt-5">
              {!answered ? (
                <Button
                  data-testid="scenario-submit-btn"
                  onClick={handleQuizSubmit}
                  disabled={!selected}
                  className="rounded-full px-6"
                >
                  Check answer
                </Button>
              ) : (
                <Button
                  data-testid="scenario-next-btn"
                  onClick={handleQuizNext}
                  className="rounded-full px-6"
                >
                  {quizIdx < questions.length - 1 ? "Next question" : "Finish"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== READING MODE =====
  return (
    <div data-testid="scenario-detail-page" className="max-w-4xl mx-auto">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/cases")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        All clinical cases
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <span
            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
            style={{
              backgroundColor: `${scenario.subject_color}20`,
              color: scenario.subject_color,
            }}
          >
            {scenario.subject_name}
          </span>
          <h1 className="text-2xl md:text-3xl font-bold mt-2 flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-primary" />
            {scenario.title}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {scenario.estimated_time} min
            </span>
            <span>{totalSections} steps</span>
          </div>
        </div>
        {scenario.completed && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-success/20 text-success">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">Done</span>
          </div>
        )}
      </div>

      {/* Step navigator */}
      <div className="glass rounded-2xl p-4 border border-border mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            Step {currentSection + 1} of {totalSections}
          </span>
          <span className="text-sm font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 scrollbar-none snap-x" data-testid="section-chips">
          {sections.map((section, i) => (
            <button
              key={i}
              onClick={() => setCurrentSection(i)}
              data-testid={`scenario-step-${i}`}
              ref={(el) => { if (el && el.classList.contains("bg-primary")) { const c = el.parentElement; c.scrollLeft = el.offsetLeft - (c.clientWidth - el.offsetWidth) / 2; } }}
              className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all snap-center shrink-0 ${
                i === currentSection
                  ? "bg-primary text-white"
                  : "bg-secondary text-muted-foreground hover:bg-secondary"
              }`}
            >
              {i + 1}. {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Section content */}
      {sectionData && (
        <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-1">{sectionData.title}</h2>
          {SECTION_HINTS[sectionData.title] && (
            <p className="text-sm text-muted-foreground mb-6">
              {SECTION_HINTS[sectionData.title]}
            </p>
          )}

          <div className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown
              components={{
                strong: ({ children }) => (
                  <strong className="text-primary font-semibold">{children}</strong>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-5 space-y-1.5 my-3">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-5 space-y-1.5 my-3">{children}</ol>
                ),
                h3: ({ children }) => (
                  <h3 className="text-base font-semibold mt-5 mb-2">{children}</h3>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground my-3">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {sectionData.content || ""}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Final-step extras */}
      {isLastSection && (scenario.key_takeaways?.length || scenario.exam_tips?.length) ? (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {scenario.key_takeaways?.length > 0 && (
            <div className="glass rounded-2xl p-5 border border-success/20 bg-success/5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-success" />
                <h3 className="font-semibold">Key takeaways</h3>
              </div>
              <ul className="space-y-2 text-sm">
                {scenario.key_takeaways.map((kt, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-success mt-1">•</span>
                    <span>{kt}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {scenario.exam_tips?.length > 0 && (
            <div className="glass rounded-2xl p-5 border border-warning/20 bg-warning/5">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-warning" />
                <h3 className="font-semibold">Exam tips</h3>
              </div>
              <ul className="space-y-2 text-sm">
                {scenario.exam_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-warning mt-1">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {/* Footer nav */}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          onClick={() => setCurrentSection((s) => Math.max(0, s - 1))}
          disabled={currentSection === 0}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        {isLastSection ? (
          <div className="flex flex-wrap gap-2 justify-end">
            {!scenario.completed && (
              <Button
                onClick={handleComplete}
                data-testid="scenario-complete-btn"
                className="rounded-full bg-success hover:bg-success/80"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark complete
              </Button>
            )}
            <Button
              data-testid="scenario-roleplay-btn"
              onClick={() => navigate(`/cases/${scenarioId}/role-play`)}
              variant="outline"
              className="rounded-full border-warning/40 text-warning hover:bg-warning/10 hover:text-warning"
            >
              <Mic className="w-4 h-4 mr-2" />
              Talk to patient
            </Button>
            {questions.length > 0 && (
              <Button
                data-testid="scenario-quiz-btn"
                onClick={() => setShowQuiz(true)}
                className="rounded-full"
              >
                <Target className="w-4 h-4 mr-2" />
                Test yourself ({questions.length})
              </Button>
            )}
          </div>
        ) : (
          <Button
            onClick={() => setCurrentSection((s) => Math.min(totalSections - 1, s + 1))}
            data-testid="scenario-next-step-btn"
            className="rounded-full"
          >
            Next step
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ScenarioDetailPage;
